import React, { useEffect, useCallback, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import cookies from "js-cookie";
import axios from "axios";
import { parseDomain } from "parse-domain";
import { setDefaultLocale } from "react-datepicker";
import { WhatsApp, LanguageOutlined } from "@mui/icons-material";


import { setSidebarSize } from "../features/layout/sidebarSizeSlice";
import { setLoggedIn } from "../features/account/loginSlice";

import avatar from "../images/Default-Avatar-Primary.webp";
import logo from "../images/trucabon-logo.png";
import logoSmall from "../images/trucabon-logo.png";
import {
  horizontalHeaderLayout,
  resetHeaderLayout,
  ROUTE_PATHS,
} from "../library/helper";

const LANG_LABEL = {
  en: "EN",
  id: "ID",
  "zh-CN": "ZH-CN",
  "zh-TW": "ZH-TW",
  "tl-PH": "FIL",
  th: "TH",
  vi: "VI",
  "ms-MY": "MS",
  ja: "JA",
};

const HeaderSection = ({ isOnboarding }) => {
  const { t, i18n } = useTranslation();
  const account = useSelector((state) => state.account.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [langHover, setLangHover] = useState(false);

  const closeMobileSidebar = useCallback(() => {
    const isVertical = document.documentElement.getAttribute("data-layout") === "vertical";
    const isPhone = window.innerWidth < 768;
    document.body.classList.remove("vertical-sidebar-enable");
    document.querySelector(".hamburger-icon")?.classList.remove("open");

    if (
      isPhone &&
      isVertical &&
      document.documentElement.getAttribute("data-sidebar-size") === "lg"
    ) {
      dispatch(setSidebarSize("sm"));
    }
  }, [dispatch]);

  useEffect(() => {
    if (isOnboarding) {
      closeMobileSidebar();
      horizontalHeaderLayout();
      return () => {
        closeMobileSidebar();
        resetHeaderLayout();
      };
    }

    resetHeaderLayout();
    return undefined;
  }, [isOnboarding, closeMobileSidebar]);

  useEffect(() => {
    if (window.innerWidth < 1024) {
      closeMobileSidebar();
    }
  }, [location.pathname, closeMobileSidebar]);

  useEffect(() => {
    const handleResize = () => {
      const isVertical = document.documentElement.getAttribute("data-layout") === "vertical";
      if (!isVertical) return;

      closeMobileSidebar();

      if (
        window.innerWidth < 768 &&
        document.documentElement.getAttribute("data-sidebar-size") !== "sm"
      ) {
        dispatch(setSidebarSize("sm"));
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch, closeMobileSidebar]);

  const onUpdateLanguage = async (lang) => {
    i18n.changeLanguage(lang);
    setDefaultLocale(lang);

    try {
      await axios({
        withCredentials: true,
        method: "POST",
        url: `${process.env.REACT_APP_BASE_SERVER}/api/v2/user-save-language`,
        data: {
          languageCode: lang,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to save language preference:", error);
    }
  };

  const onClickHamburgerIcon = () => {
    const isVertical = document.documentElement.getAttribute("data-layout") === "vertical";
    if (!isVertical) return;

    document.body.classList.remove("vertical-sidebar-enable");
    document.querySelector(".hamburger-icon")?.classList.toggle("open");

    const current = document.documentElement.getAttribute("data-sidebar-size");
    if (current === "sm") {
      dispatch(setSidebarSize("lg"));
    } else {
      dispatch(setSidebarSize("sm"));
    }
  };

  const rawLang = i18n.resolvedLanguage || i18n.language || "en";
  const baseLang = String(rawLang).split("-")[0];
  const currentLangLabel =
    LANG_LABEL[rawLang] || LANG_LABEL[baseLang] || baseLang.toUpperCase();

  const baseUrl = `${process.env.REACT_APP_BASE_SERVER}/uploads/client`;
  const avatarSrc =
    account.client_image && account.client_image !== "1.png"
      ? `${baseUrl.replace(/\/$/, "")}/${account.client_image.replace(/^\//, "")}`
      : avatar;

  return (
    <>
      <header id="page-topbar">
        <div className="layout-width">
          <div className="navbar-header">
            <div className="d-flex">
              <div className="navbar-brand-box horizontal-logo">
                <Link to={ROUTE_PATHS.dashboard} className="logo logo-dark">
                  <span className="logo-sm">
                    <img src={logoSmall} alt="" height="25" />
                  </span>
                  <span className="logo-lg">
                    <img src={logo} alt="" height="65" />
                  </span>
                </Link>

                <Link to={ROUTE_PATHS.dashboard} className="logo logo-light">
                  <span className="logo-sm">
                    <img src={logoSmall} alt="" height="25" />
                  </span>
                  <span className="logo-lg">
                    <img src={logo} alt="" height="65" />
                  </span>
                </Link>
              </div>

              {!!account.client_id && (
                <button
                  type="button"
                  className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
                  id="topnav-hamburger-icon"
                  onClick={onClickHamburgerIcon}
                >
                  <span className="hamburger-icon">
                    <span></span>
                    <span></span>
                    <span></span>
                  </span>
                </button>
              )}
            </div>

            <div className="d-flex align-items-center flex-shrink-0 gap-2 gap-sm-3">
              {account.client_onboard === 1 && account?.client_subscription === 1 && (
                <a
                  href="https://wa.me/6282228886906?text=Hello%20Trucarbon,%20I%20want%20to%20upgrade%20to%20Premium."
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 me-4"
                  style={{ whiteSpace: "nowrap" }}
                >
                  <WhatsApp fontSize="small" />
                  <span className="d-none d-sm-inline">{t("menu.ctaUpgrade")}</span>
                </a>
              )}

              <div
                className="dropdown topbar-head-dropdown header-item"
                style={{ width: "6rem" }}
              >
                <button
                  className="btn d-inline-flex align-items-center"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                  onMouseEnter={() => setLangHover(true)}
                  onMouseLeave={() => setLangHover(false)}
                  style={{
                    gap: 10,
                    height: 40,
                    paddingLeft: 12,
                    paddingRight: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  <LanguageOutlined
                    fontSize="small"
                    sx={{ color: langHover ? "#fff" : "inherit" }}
                  />
                  <span
                    style={{
                      lineHeight: 1,
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      color: langHover ? "#fff" : "inherit",
                    }}
                  >
                    {currentLangLabel}
                  </span>
                </button>

                <div className="dropdown-menu dropdown-menu-start">
                  <button
                    onClick={() => onUpdateLanguage("en")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="en"
                    title="English"
                  >
                    <span className="align-middle">English</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("id")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="id"
                    title="Bahasa Indonesia"
                  >
                    <span className="align-middle">Bahasa Indonesia</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("zh-CN")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="zh-CN"
                    title="Chinese (Simplified)"
                  >
                    <span className="align-middle">Chinese (Simplified)</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("zh-TW")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="zh-TW"
                    title="Chinese (Traditional)"
                  >
                    <span className="align-middle">Chinese (Traditional)</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("tl-PH")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="tl-PH"
                    title="Filipino"
                  >
                    <span className="align-middle">Filipino</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("th")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="th"
                    title="Thai"
                  >
                    <span className="align-middle">Thai</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("vi")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="vi"
                    title="Vietnamese"
                  >
                    <span className="align-middle">Vietnamese</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("ms-MY")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="ms-MY"
                    title="Bahasa Melayu"
                  >
                    <span className="align-middle">Bahasa Melayu</span>
                  </button>

                  <button
                    onClick={() => onUpdateLanguage("ja")}
                    className="dropdown-item notify-item language py-2"
                    data-lang="ja"
                    title="Japanese"
                  >
                    <span className="align-middle">Japanese</span>
                  </button>
                </div>
              </div>

              <div className="dropdown header-item topbar-user topbar-user-custom">
                <button
                  type="button"
                  className="btn"
                  id="page-header-user-dropdown"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span className="d-flex align-items-center">
                    <img
                      className="rounded-circle header-profile-user"
                      style={{ objectFit: "scale-down" }}
                      src={avatarSrc}
                      alt="Avatar"
                    />
                    <span className="text-start ms-xl-2">
                      <span className="d-none d-xl-inline-block fw-medium user-name-text user-name-text-custom">
                        {account.user_name !== undefined ? account.user_name : ""}
                      </span>
                      <span className="d-none d-xl-block fs-12 text-muted user-name-sub-text">
                        {account.client_name !== undefined ? account.client_name : ""}
                      </span>
                    </span>
                  </span>
                </button>

                <div className="dropdown-menu dropdown-menu-end">
                  <div className="dropdown-divider"></div>
                  <button
                    onClick={async () => {
                      const url = process.env.REACT_APP_BASE_SERVER;
                      try {
                        await axios({
                          method: "POST",
                          url: `${url}/account/logout`,
                          withCredentials: true,
                        });
                      } catch (_) {
                        // Fall through to local cleanup even if the server call fails.
                      }
                      const signedInKey = `${new URL(url).hostname}_signedIn`;
                      cookies.remove(signedInKey);

                      const hostname = window.location.hostname;
                      const parsedDomain = parseDomain(hostname);
                      const domain = parsedDomain.domain ?? "";
                      const tlds = parsedDomain.topLevelDomains ?? [];
                      cookies.remove(signedInKey, {
                        domain: [domain, ...tlds].join("."),
                      });

                      dispatch(setLoggedIn(false));
                      navigate(ROUTE_PATHS.dashboard);
                    }}
                    className="dropdown-item"
                  >
                    <i className="mdi mdi-logout text-muted fs-16 align-middle me-1"></i>
                    <span className="align-middle" data-key="t-logout">
                      {t("menu.account.label3")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div
        className="vertical-overlay"
        onClick={closeMobileSidebar}
        aria-hidden="true"
      ></div>
    </>
  );
};

export default HeaderSection;
